import React, { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import moment from "moment";
import { RiArrowUpLine, RiArrowDownLine, RiExternalLinkLine } from "@remixicon/react";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import { useNews, useOilPrices, seriesStats, splitHeadline, stripHtml } from "../../hooks/useMarketData";

// Mirrors ordr-flutter lib/market_updates: daily/weekly/monthly WTI + Brent
// chart on top, "Market news" list underneath.
const INTERVALS = [
	{ key: "daily", label: "Daily" },
	{ key: "weekly", label: "Weekly" },
	{ key: "monthly", label: "Monthly" },
];

// The feed returns ~100 headlines; render them a page at a time.
const NEWS_PAGE = 12;
const WTI_COLOR = "#333333";
const BRENT_COLOR = "#7f9a3a";

export default function MarketUpdates() {
	const [interval, setIntervalKey] = useState("daily");
	const { loading, wti, brent } = useOilPrices(interval);
	const { loading: newsLoading, news } = useNews();
	const [newsShown, setNewsShown] = useState(NEWS_PAGE);

	const chartData = useMemo(() => {
		const byDate = {};
		(wti?.points || []).forEach((p) => (byDate[p.date] = { date: p.date, wti: p.value }));
		(brent?.points || []).forEach((p) => (byDate[p.date] = { ...(byDate[p.date] || { date: p.date }), brent: p.value }));
		return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
	}, [wti, brent]);

	const isSample = (wti && !wti.live) || (brent && !brent.live);
	const dateFmt = interval === "monthly" ? "MMM YY" : "MMM D";

	return (
		<div>
			<PageHeader title="Market updates" subtitle="Crude benchmarks and fuel industry news to help you time your bid requests" />

			<div className="row g-3 mb-3">
				<div className="col-md-6">
					<PriceTile series={wti} color={WTI_COLOR} loading={loading} />
				</div>
				<div className="col-md-6">
					<PriceTile series={brent} color={BRENT_COLOR} loading={loading} />
				</div>
			</div>

			<div className="card p-4 mb-4">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
					<div>
						<h6 className="section-title">Crude oil prices</h6>
						<div className="text-muted small">US dollars per barrel{isSample ? " · showing sample data (live feed unavailable)" : ""}</div>
					</div>
					<div className="segmented">
						{INTERVALS.map((i) => (
							<button key={i.key} type="button" className={`segmented-btn${interval === i.key ? " active" : ""}`} onClick={() => setIntervalKey(i.key)}>
								{i.label}
							</button>
						))}
					</div>
				</div>
				{loading ? (
					<Loader />
				) : (
					<ResponsiveContainer width="100%" height={280}>
						<LineChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#e6e7ea" vertical={false} />
							<XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8a8f98" }} tickFormatter={(d) => moment(d).format(dateFmt)} minTickGap={24} />
							<YAxis tick={{ fontSize: 12, fill: "#8a8f98" }} domain={["dataMin - 2", "dataMax + 2"]} tickFormatter={(v) => `$${Math.round(v)}`} />
							<Tooltip
								formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name]}
								labelFormatter={(d) => moment(d).format("MMM D, YYYY")}
								contentStyle={{ borderRadius: 10, border: "1px solid #e6e7ea" }}
							/>
							<Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} />
							<Line type="monotone" name="WTI" dataKey="wti" stroke={WTI_COLOR} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
							<Line type="monotone" name="Brent" dataKey="brent" stroke={BRENT_COLOR} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
						</LineChart>
					</ResponsiveContainer>
				)}
			</div>

			<div className="d-flex justify-content-between align-items-center mb-3">
				<h6 className="section-title">Market news</h6>
				{news.length ? <span className="text-muted small">{news.length} stories</span> : null}
			</div>
			{newsLoading ? (
				<Loader />
			) : news.length === 0 ? (
				<EmptyState title="No news available right now" subtitle="Check back later for the latest fuel market headlines." />
			) : (
				<>
				<div className="row g-3">
					{news.slice(0, newsShown).map((n, idx) => {
						const { headline, source } = splitHeadline(n);
						const summary = stripHtml(n.summary);
						return (
							<div className="col-md-6" key={n.newsUrl || idx}>
								<a className="card news-tile h-100" href={n.newsUrl} target="_blank" rel="noopener noreferrer">
									<span className={`news-thumb thumb-${idx % 3}`} />
									<span className="news-tile-body">
										<span className="news-tile-title">{headline}</span>
										{summary && summary !== headline ? <span className="news-tile-summary">{summary}</span> : null}
										<span className="news-tile-meta">
											{source || "Read article"}
											{n.pubDate ? ` · ${moment(n.pubDate).fromNow()}` : ""}
											<RiExternalLinkLine size={12} />
										</span>
									</span>
								</a>
							</div>
						);
					})}
				</div>
				{newsShown < news.length ? (
					<div className="text-center mt-3">
						<button type="button" className="btn-outline" onClick={() => setNewsShown((n) => n + NEWS_PAGE)}>
							Load more stories
						</button>
					</div>
				) : null}
				</>
			)}
		</div>
	);
}

function PriceTile({ series, color, loading }) {
	const { last, change, pct } = seriesStats(series);
	const up = change >= 0;
	return (
		<div className="card p-3 d-flex flex-row align-items-center gap-3">
			<span style={{ width: 10, height: 40, borderRadius: 4, background: color }} />
			<div className="flex-grow-1">
				<div className="text-muted small fw-semibold text-uppercase">{series?.name || "Crude"}</div>
				<div className="fs-4 fw-bold">{loading || last == null ? "–" : `$${last.toFixed(2)}`}</div>
			</div>
			{!loading && last != null ? (
				<span className={`status-pill no-dot ${up ? "success" : "danger"}`} style={{ gap: 2 }}>
					{up ? <RiArrowUpLine size={12} /> : <RiArrowDownLine size={12} />}
					{Math.abs(change).toFixed(2)} ({Math.abs(pct).toFixed(2)}%)
				</span>
			) : null}
		</div>
	);
}
